import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Box,
  TablePagination,
  Tooltip,
} from "@mui/material";
import TableSortLabel from "@mui/material/TableSortLabel";
import { visuallyHidden } from "@mui/utils";
import useToast from "../../../helpers/useToast";
import { toast } from "react-toastify";
import useHeaders from "../../../helpers/useHeaders";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import AdminQuestionModal from "./AdminQuestionModal";
import { setSelectedId } from "../../../redux/slice/authSlice";
import {
  getAllCustomQuestions,
  deleteCustomQuestion,
} from "../../../api/typeFormQuestionApi";
import { useOutletContext } from "react-router-dom";
import useEncryption from "../../../services/useEncryption";
import DeleteDialogBox from "../../../components/dialogBox/DeleteDialogBox";
import { createAuditLogs } from "../../../api/providerApi/providerApi";

const headCells = [
  { id: "questionVariable", label: "Question Variable", disablePadding: true },
  { id: "customVariable", label: "Custom Variable", disablePadding: false },
  { id: "actions", label: "Actions", disablePadding: false },
];

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function EnhancedTableHead(props) {
  const {
    onSelectAllClick,
    order,
    orderBy,
    numSelected,
    rowCount,
    onRequestSort,
  } = props;

  const createSortHandler = (property) => (event) =>
    onRequestSort(event, property);

  return (
    <TableHead>
      <TableRow>
        {/* <TableCell padding="checkbox">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell> */}

        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  numSelected: PropTypes.number.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
  onSelectAllClick: PropTypes.func.isRequired,
  onRequestSort: PropTypes.func.isRequired,
};

export const TypeformQuestion = ({ search, update, setUpdate }) => {
  const { decryptData } = useEncryption();
  const dispatch = useDispatch();
  const headers = useHeaders();

  const toastOptions = useToast();

  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);

  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("createdAt");

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState(null);

  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [loading, setLoading] = useOutletContext();

  const handleDeleteClose = () => {
    setIsDeleteOpen(false);
    setQuestionToDelete(null);
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, search, update]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getAllCustomQuestions(page + 1, rowsPerPage, search);
      const decrypted = decryptData(res?.data?.data);

      setData(decrypted.questions);
      setTotalCount(decrypted.totalDocuments);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error("Error fetching questions", error);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const allIds = data.map((row) => row._id);
      setSelected(allIds);
      dispatch(setSelectedId(["All"]));
      return;
    }
    setSelected([]);
    dispatch(setSelectedId([]));
  };

  const handleClick = (event, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [...selected];

    if (selectedIndex === -1) newSelected.push(id);
    else newSelected.splice(selectedIndex, 1);

    setSelected(newSelected);
    dispatch(setSelectedId(newSelected));
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;

  const handleEdit = (row) => {
    setEditData(row);
    setOpenEdit(true);
  };

  const handleDelete = (row) => {
    setQuestionToDelete(row);
    setIsDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      console.log("Deleting:", questionToDelete);

      const response = await deleteCustomQuestion(questionToDelete._id);
      toast.success(response.data.message, toastOptions);

      handleDeleteClose();
      fetchData();
    } catch (error) {
      console.error("Delete failed", error);
      toast.error(
        error?.response?.data?.message || "Delete failed",
        toastOptions
      );
    }
  };

  return (
    <div className="tableDesigned">
      <Box>
        <Paper>
          <TableContainer>
            <Table>
              <EnhancedTableHead
                // numSelected={selected.length}
                numSelected={0}
                order={order}
                orderBy={orderBy}
                // onSelectAllClick={handleSelectAllClick}
                onSelectAllClick={() => {}}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
              />

              <TableBody>
                {data.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      style={{ padding: "40px 0" }}
                    >
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  data.map((row, index) => {
                    const isItemSelected = isSelected(row._id);
                    const labelId = `table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        key={row._id}
                        selected={isItemSelected}
                        sx={{ cursor: "pointer" }}
                      >
                        {/* <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onClick={(e) => handleClick(e, row._id)}
                          inputProps={{ "aria-labelledby": labelId }}
                        />
                      </TableCell> */}

                        <TableCell padding="none">
                          {row.questionVariable}
                        </TableCell>
                        <TableCell>{row.customVariable}</TableCell>

                        <TableCell>
                          <div className="btn-groups">
                            <button
                              onClick={() => handleEdit(row)}
                              className="actionbuttons"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="25"
                                viewBox="0 0 24 25"
                                fill="none"
                              >
                                <path
                                  d="M3.99939 21.4801H20.499M19.2362 4.39438L20.0851 5.24337C20.8662 6.02442 20.8662 7.29075 20.0851 8.0718L8.84933 19.3076C8.62978 19.5271 8.36213 19.6926 8.06758 19.7907L3.94804 21.1639C3.55716 21.2942 3.18529 20.9223 3.31558 20.5315L4.68876 16.4119C4.78695 16.1174 4.95237 15.8497 5.17192 15.6302L16.4077 4.39437C17.1888 3.61333 18.4551 3.61333 19.2362 4.39438Z"
                                  stroke="#35353A"
                                  strokeWidth="1.25"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDelete(row)}
                              className="actionbuttons"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="24"
                                height="25"
                                viewBox="0 0 24 25"
                                fill="none"
                              >
                                <path
                                  d="M18 7.48047H18.5C19.0523 7.48047 19.5 7.03275 19.5 6.48047V5.98047C19.5 5.42819 19.0523 4.98047 18.5 4.98047H15.5M18 7.48047V19.4805C18 20.5851 17.1046 21.4805 16 21.4805H8C6.89543 21.4805 6 20.5851 6 19.4805V7.48047M18 7.48047H6M15.5 4.98047V4.48047C15.5 3.3759 14.6046 2.48047 13.5 2.48047H10.5C9.39543 2.48047 8.5 3.3759 8.5 4.48047V4.98047M15.5 4.98047H8.5M6 7.48047H5.5C4.94772 7.48047 4.5 7.03275 4.5 6.48047V5.98047C4.5 5.42819 4.94772 4.98047 5.5 4.98047H8.5M10 10.9805V17.9805M14 10.9805V17.9805"
                                  stroke="#35353A"
                                  strokeWidth="1.25"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <TablePagination
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Box>
        </Paper>
      </Box>
      <DeleteDialogBox
        isDeleteOpen={isDeleteOpen}
        onDeleteClose={handleDeleteClose}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        description="Are you sure you want to delete this question?"
      />
      <AdminQuestionModal
        open={openEdit}
        handleClose={() => setOpenEdit(false)}
        data={editData}
        setUpdate={setUpdate}
      />
    </div>
  );
};
